/**
 * Reine, testbare Navigations-Logik des Aufgaben-Features (A5).
 *
 * Baut aus dem 3-Zustands-Gate ({@link resolveTasksGateState}), den Zählern und
 * den servergelieferten `AllowedActions` die konkreten Nav-/Tab-Objekte für die
 * Sidebar (`xds-nav-list`-`XdsNavItem`), die In-Page-Reiter (`xds-tabs`-`XdsTab`)
 * und die Rollen-Landing (06-ENTRYPOINT-NAV.md §1/§2/§4).
 *
 * Enthält KEINE Angular-Abhängigkeit → 1:1 unit-testbar (Pflicht-Negativtest §3.3).
 */
import { XdsBadgeVariant } from 'src/app/shared/ui/badge/badge.component';
import { XdsNavItem } from 'src/app/shared/ui/nav-list/nav-list.component';
import { XdsTab } from 'src/app/shared/ui/tabs/tabs.component';
import { TaskAllowedAction } from '../models';
import { resolveTasksGateState, TasksGateInput, TasksGateState } from '../gate';

/** i18n-Key des Sidebar-Labels („Aufgaben"/„Tasks"). */
export const TASKS_NAV_LABEL_KEY = 'TASKS';
/** FontAwesome-Icon des Aufgaben-Punkts (bewusst ≠ Planungs-Kalender-Icon). */
export const TASKS_NAV_ICON = 'fa-light fa-list-check';
/** Nav-Id des Aufgaben-Eintrags — auch als `activeId`-Wert. */
export const TASKS_NAV_ID = 'tasks';

/** Offene/überfällige Zähler für die Nav-Badge (06 §1). */
export interface TasksNavCounts {
  /** Ungelesene/offene Aufgaben im Scope (weglassen der Badge bei 0). */
  openCount: number;
  /** Überfällige Aufgaben (zählen mit; färben seit AP-14 / E-06 nichts mehr). */
  overdueCount: number;
}

/**
 * Baut das `XdsNavItem` für den Sidebar-Eintrag — oder `null`, wenn der Eintrag
 * laut Gate **versteckt** ist (dann gar nicht ins `groups.items[]` aufnehmen).
 *
 * Badge-Regeln (06 §1):
 *  - Bei `openCount === 0 && overdueCount === 0` → **kein** Badge (Feld weglassen).
 *  - `badgeVariant='info'` immer (AP-14 / E-06, Monochrom). Bis dahin schaltete
 *    `overdueCount > 0` auf `'danger'`.
 *  - Die angezeigte Zahl ist die Summe sichtbarer offener/überfälliger Aufgaben.
 */
export function buildTasksNavItem(
  gate: TasksGateState,
  counts?: TasksNavCounts | null,
): XdsNavItem | null {
  if (gate === 'hidden') return null;

  const item: XdsNavItem = {
    id: TASKS_NAV_ID,
    labelKey: TASKS_NAV_LABEL_KEY,
    icon: TASKS_NAV_ICON,
  };

  const open = Math.max(0, counts?.openCount ?? 0);
  const overdue = Math.max(0, counts?.overdueCount ?? 0);
  const badgeCount = Math.max(open, overdue);
  if (badgeCount > 0) {
    item.badge = String(badgeCount);
    item.badgeVariant = 'info';
  }
  return item;
}

/** Kurzform: liefert das Nav-Item direkt aus dem Gate-Input. */
export function resolveTasksNavItem(
  input: TasksGateInput,
  counts?: TasksNavCounts | null,
): XdsNavItem | null {
  return buildTasksNavItem(resolveTasksGateState(input), counts);
}

/** Etappen-/Rollout-Flags für die In-Page-Reiter (DEC-01/DEC-17/DEC-02). */
export interface TasksTabsInput {
  /** E3 aktiv → Reiter „Routinen" (DEC-17). */
  routinesEnabled: boolean;
  /** E4 aktiv → Reiter „Projekte" (DEC-02, NICHT gebaut). */
  projectsEnabled: boolean;
  /**
   * Servergelieferte `AllowedActions` des Scopes. „Lage" erscheint NUR mit `ViewOperations`;
   * fehlt sie (älterer Server), erscheint am selben Platz „Compliance", wenn `ViewCompliance`
   * enthalten ist (Server-Autorität, 06 §2 Sicherheitsnetz; L1/L5).
   */
  allowedActions?: readonly (TaskAllowedAction | string)[] | null;
  /** Optionale Pro-Reiter-Zähler (id → {openCount, overdueCount}). */
  badges?: Record<string, TasksNavCounts | undefined>;
  /**
   * Tarif-Stufe „Routinen & Vorlagen" (`feature.tasks_routines`, ab Plus) gebucht?
   *
   * Bewusst **optional mit Default `true`**: der Reiter hängt bereits an
   * `routinesEnabled` (Rollout) und an den Rechten. Ein fehlender Wert bedeutet
   * „Tarif-Stand unbekannt" — dann nicht ausblenden (fail-open, gleiche Regel wie
   * `TasksTierGuard` und `TasksFeatureGuard`). Das Backend lehnt die Routinen-Requests
   * ohnehin mit 403 `TASK_ROUTINES_NOT_IN_PLAN` ab.
   */
  routinesInPlan?: boolean;
  /** Analog für „Compliance & Nachweis" (`feature.tasks_compliance`, nur Pro). */
  complianceInPlan?: boolean;
  /**
   * Firmen-Schalter `routinesEnabled` aus den Aufgaben-Einstellungen (09/2026). Schränkt
   * zusätzlich zum Tarif ein, hebt ihn nie auf. Optional, fehlend = an (fail-open — die
   * Defaults der Einstellungen sind „an", der Server lehnt ohnehin mit 403
   * `TASK_SETTING_DISABLED` ab).
   */
  routinesSettingEnabled?: boolean;
  /** Analog: Firmen-Schalter `complianceEnabled`. */
  complianceSettingEnabled?: boolean;
  /**
   * AP-45 / F-8 — Reiter „Durchlauf". Hängt an „Aufträge nutzen" UND „Durchlauf-Auswertung"
   * (`projectExtensionFlags().throughputViewEnabled`, inkl. Modul-Hauptschalter). Anders als die Schalter oben
   * **fehlend = aus**: die Grundversion ist standardmäßig aus, und aus heißt unsichtbar (E-05).
   */
  throughputEnabled?: boolean;
  /**
   * Reiter „Auswertung" (Team je Person, 09.10.2026). Hängt am Firmenschalter
   * `personReportEnabled` UND an `ViewPersonReport` in den `AllowedActions` — beides vom
   * Aufrufer über {@link canViewTeamReportTab} verknüpft. **Fehlend = aus** (personenbezogen,
   * also fail-closed).
   */
  teamReportEnabled?: boolean;
}

interface TabSpec {
  id: string;
  labelKey: string;
  icon: string;
}

/**
 * Reiter „Lage" (L1, 09.10.2026) — ersetzt den Reiter „Compliance" an derselben Stelle.
 * Ziel `/tasks/lage`; die bisherige Compliance-Seite ist die Unterseite `/tasks/lage/nachweise`.
 */
export const LAGE_TAB_ID = 'lage';
/**
 * Rückfall älterer Server ohne `ViewOperations`: der Reiter heißt weiter „Compliance" und führt
 * direkt auf die Unterseite „Nachweise" (die Seite „Lage" selbst darf er nicht öffnen).
 */
export const COMPLIANCE_TAB_ID = 'compliance';

const LAGE_TAB_SPEC: TabSpec = { id: LAGE_TAB_ID, labelKey: 'TASKS_TAB_LAGE', icon: 'fa-light fa-gauge' };
const COMPLIANCE_TAB_SPEC: TabSpec = {
  id: COMPLIANCE_TAB_ID,
  labelKey: 'TASKS_TAB_COMPLIANCE',
  icon: 'fa-light fa-clipboard-check',
};

/**
 * Reiter-Reihenfolge + Icons (06 §2). Der Platz `lage` wird je nach Server zu „Lage" oder zum
 * Rückfall „Compliance" ({@link resolveLageTabMode}).
 */
const TAB_SPECS: readonly TabSpec[] = [
  { id: 'overview', labelKey: 'TASKS_TAB_OVERVIEW', icon: 'fa-light fa-list-check' },
  { id: 'routines', labelKey: 'TASKS_TAB_ROUTINES', icon: 'fa-light fa-repeat' },
  LAGE_TAB_SPEC,
  { id: 'throughput', labelKey: 'TASKS_TAB_THROUGHPUT', icon: 'fa-light fa-bars-progress' },
  { id: 'team', labelKey: 'TASKS_TAB_TEAM_REPORT', icon: 'fa-light fa-chart-user' },
  { id: 'projects', labelKey: 'TASKS_TAB_PROJECTS', icon: 'fa-light fa-diagram-project' },
];

/**
 * Surface-Aktion „ViewOperations" (B5, BE `d511d557`) in den `AllowedActions` von
 * `GET Tasks/scope`? Leitung (Admin, Manager, Teamleitung) mit mehr als eigener Sicht — an
 * keinen Tarif und keinen Schalter gebunden (L5). Case-insensitiv, null-sicher.
 */
export function canViewOperations(
  allowedActions?: readonly (TaskAllowedAction | string)[] | null,
): boolean {
  if (!allowedActions) return false;
  return allowedActions.some((a) => String(a).toLowerCase() === 'viewoperations');
}

/** Eingaben der Nachweis-Bedingung (heutige Compliance-Route). */
export interface NachweiseAccessInput {
  allowedActions?: readonly (TaskAllowedAction | string)[] | null;
  /** Tarif-Merkmal `compliance`; fehlend = unbekannt = an (fail-open wie bisher). */
  complianceInPlan?: boolean;
  /** Firmenschalter `complianceEnabled`; fehlend = an (fail-open wie bisher). */
  complianceSettingEnabled?: boolean;
}

/**
 * Darf der Nutzer die Unterseite „Nachweise" (vormals Compliance) sehen? Dieselben drei
 * Bedingungen wie die Route `lage/nachweise`: `ViewCompliance` UND Tarif UND Schalter. Gilt auch
 * für das Export-Menü der Seite „Lage" und (F10) die Karte „Pflichten".
 */
export function canViewNachweise(input: NachweiseAccessInput): boolean {
  return (
    canViewComplianceTab(input.allowedActions) &&
    input.complianceInPlan !== false &&
    input.complianceSettingEnabled !== false
  );
}

/**
 * Welcher Reiter steht am Platz „Lage"?
 *  - `ViewOperations` → `'lage'` (Seite „Lage", unabhängig von Tarif und Schalter, L5)
 *  - sonst Nachweis-Bedingung erfüllt → `'compliance'` (älterer Server; Ziel „Nachweise")
 *  - sonst `null` → kein Reiter
 */
export function resolveLageTabMode(input: NachweiseAccessInput): 'lage' | 'compliance' | null {
  if (canViewOperations(input.allowedActions)) return LAGE_TAB_ID;
  if (canViewNachweise(input)) return COMPLIANCE_TAB_ID;
  return null;
}

/**
 * Route eines Reiters. `lage` → `/tasks/lage`, der Rückfall `compliance` direkt auf
 * `/tasks/lage/nachweise` (die alte Adresse `/tasks/compliance` leitet zwar auch dorthin, aber
 * ohne Umweg bleibt der Verlauf sauber), alle übrigen `/tasks/<id>`.
 */
export function tasksTabRoute(id: string): string[] {
  if (id === LAGE_TAB_ID) return ['/tasks', 'lage'];
  if (id === COMPLIANCE_TAB_ID) return ['/tasks', 'lage', 'nachweise'];
  return ['/tasks', id];
}

/**
 * Aktiver Reiter, abgeglichen mit den sichtbaren Reitern: `lage` und `compliance` sind derselbe
 * Platz. Die Nachweis-Seite meldet sich als `compliance` — mit `ViewOperations` ist dann „Lage"
 * hervorgehoben (die Seite liegt unter `/tasks/lage/nachweise`), ohne der Rückfall-Reiter.
 */
export function resolveActiveTasksTab(activeId: string, tabs: readonly { id: string }[]): string {
  if (activeId !== LAGE_TAB_ID && activeId !== COMPLIANCE_TAB_ID) return activeId;
  const slot = tabs.find((t) => t.id === LAGE_TAB_ID || t.id === COMPLIANCE_TAB_ID);
  return slot ? slot.id : activeId;
}

/** true, wenn `ViewCompliance` in den `AllowedActions` steht (case-insensitiv). */
export function canViewComplianceTab(
  allowedActions?: readonly (TaskAllowedAction | string)[] | null,
): boolean {
  if (!allowedActions) return false;
  return allowedActions.some(
    (a) => String(a).toLowerCase() === TaskAllowedAction.ViewCompliance.toLowerCase(),
  );
}

/**
 * Reiter „Auswertung" sichtbar? Firmenschalter an (`true`, nicht nur „nicht false") UND
 * `ViewPersonReport` vom Server. Unbekannte Einstellungen oder fehlende Aktion → aus.
 */
export function canViewTeamReportTab(
  personReportEnabled: boolean | null | undefined,
  allowedActions?: readonly (TaskAllowedAction | string)[] | null,
): boolean {
  if (personReportEnabled !== true || !allowedActions) return false;
  return allowedActions.some((a) => String(a).toLowerCase() === 'viewpersonreport');
}

/**
 * Baut die sichtbaren In-Page-Reiter (`xds-tabs`). „Übersicht" immer; „Routinen"
 * nur bei E3; „Compliance" nur bei `ViewCompliance` ∈ AllowedActions; „Projekte"
 * nur bei E4. Nicht-sichtbare Reiter werden **gar nicht** ins Array aufgenommen
 * (kein disabled-Slot, 06 §2).
 *
 * Seit der Plan-Freigabe (08/2026) kommt je Zusatzreiter eine **Tarif**-Bedingung hinzu
 * (`routinesInPlan`/`complianceInPlan`). Sie ist eine zusätzliche UND-Bedingung, kein
 * Ersatz: Rollout-Flag, Recht und Tarif müssen alle drei zutreffen. Ein nicht gebuchter
 * Reiter wird ausgeblendet und nicht gesperrt — ein sichtbares Schloss auf einer Route,
 * die der Nutzer nie öffnen kann, hilft niemandem (Upsell läuft über die Abo-Seite).
 *
 * Seit den Aufgaben-Einstellungen (09/2026) kommt als vierte UND-Bedingung der
 * Firmen-Schalter hinzu (`routinesSettingEnabled`/`complianceSettingEnabled`) — ebenfalls
 * ausgeblendet, nicht gesperrt, und fehlend = an.
 */
export function buildTasksTabs(input: TasksTabsInput): XdsTab[] {
  const lageMode = resolveLageTabMode(input);
  return TAB_SPECS.map((spec) =>
    spec.id === LAGE_TAB_ID && lageMode === COMPLIANCE_TAB_ID ? COMPLIANCE_TAB_SPEC : spec,
  ).filter((spec) => {
    switch (spec.id) {
      case 'overview':
        return true;
      case 'routines':
        return (
          input.routinesEnabled &&
          input.routinesInPlan !== false &&
          input.routinesSettingEnabled !== false
        );
      // L1/L5: „Lage" mit ViewOperations, sonst Rückfall „Compliance" (Nachweis-Bedingung).
      case LAGE_TAB_ID:
      case COMPLIANCE_TAB_ID:
        return lageMode !== null;
      case 'throughput':
        return input.throughputEnabled === true;
      case 'team':
        return input.teamReportEnabled === true;
      case 'projects':
        return input.projectsEnabled;
      default:
        return false;
    }
  }).map((spec) => {
    const tab: XdsTab = { id: spec.id, labelKey: spec.labelKey, icon: spec.icon };
    const badge = input.badges?.[spec.id];
    const open = badge ? Math.max(0, badge.openCount) : 0;
    const overdue = badge ? Math.max(0, badge.overdueCount) : 0;
    const count = Math.max(open, overdue);
    if (count > 0) {
      tab.badge = String(count);
      // AP-14 / E-06: grau, auch bei Überfälligen (vorher `'danger'`).
      tab.badgeVariant = 'neutral' as XdsBadgeVariant;
    }
    return tab;
  });
}

// ─── Rollen-Landing (06 §4) ─────────────────────────────────────────────────

export type TasksRole = 'admin' | 'manager' | 'teamLeader' | 'employee' | 'terminal';
/** Landing-Route relativ zum `tasks`-Bereich. */
export type TasksLanding = 'overview' | 'my' | 'shift-start';

/**
 * Rollen-Landing beim Klick auf „Aufgaben" (06 §4):
 *  - Admin/Manager/TeamLeader → `overview` (S1; TeamLeader server-scoped auf Team)
 *  - Employee → `my` (S3)
 *  - Terminal → `shift-start` (S7; kein freier Nav-Zugriff)
 */
export function resolveTasksLanding(role: TasksRole): TasksLanding {
  switch (role) {
    case 'employee':
      return 'my';
    case 'terminal':
      return 'shift-start';
    case 'admin':
    case 'manager':
    case 'teamLeader':
    default:
      return 'overview';
  }
}
